import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import { WithMember } from "@quintype/components";
import { SocialFollow } from "../basic/social-follow/social-follow";
import { CustomLink } from "../custom-link";
import { ImageFallback } from "../basic/image-fallback";
import { Menu } from "./menu";
import { UserProfile } from "./user-profile";
import "./header-helper-components.m.css";
import { getTextContrast } from "../../utils";

export const getMenuData = (menu, menuGroup, domainSlug) => {
  const filteredMenu = domainSlug
    ? menu.filter(item => item["menu-group-slug"] === `header-menu-${domainSlug}`)
    : menu.filter(item => item["menu-group-slug"] === menuGroup);
  if (menuGroup === "sidebar-menu") {
    if (domainSlug) return getMenuData(menu, `sidebar-menu-${domainSlug}`);
    if (filteredMenu.length === 0) return getMenuData(menu, "default");
  }
  return filteredMenu;
};

export const CtaWrapper = props => {
  const {
    socialLinks,
    publisherTheme,
    customCssClasses = "",
    isSingleLayer,
    enableAccesstype,
    socialFollowStyle,
    pageBuilderConfig
  } = props;

  const headerClass = isSingleLayer ? "single-layer-header" : "header";
  const pbHeaderConfig = get(pageBuilderConfig, ["header"], {});
  const socialFollowBgColorApplied = isSingleLayer
    ? pbHeaderConfig.primaryBackgroundColor
    : pbHeaderConfig.secondaryBackgroundColor;
  const textContrast = getTextContrast(socialFollowBgColorApplied);
  return (
    <>
      {enableAccesstype && (
        <CustomLink
          styleName={`${headerClass} ${customCssClasses}`}
          href="/subscription"
          data-test-id="header-subscribe"
        >
          Subscribe
        </CustomLink>
      )}
      <SocialFollow
        contrastAgainstBg={textContrast}
        hasBackground={true}
        socialStyle={socialFollowStyle}
        socialLinks={socialLinks}
        color={publisherTheme["header_icon_color"] || "#000"}
        customCssClasses={`${headerClass} ${customCssClasses}`}
      />
    </>
  );
};
CtaWrapper.propTypes = {
  socialLinks: PropTypes.object,
  publisherTheme: PropTypes.object,
  customCssClasses: PropTypes.string,
  isSingleLayer: PropTypes.bool,
  socialStyle: PropTypes.string,
  enableAccesstype: PropTypes.bool,
  socialFollowStyle: PropTypes.string,
  pageBuilderConfig: PropTypes.object
};

export const LogoWrapper = props => {
  const secondaryLogo = get(props, ["headerSecondaryLogo"], "");
  // Setting sketchesHost as the logo handler for now for LogoWrapper and Monogram and Footer.
  // Once subdomain changes are also in place, we may need to check this
  // how this going to playout.
  const sketchesHost = useSelector(state => get(state, ["qt", "config", "sketches-host"], "/"));
  const logoUrl = useSelector(state => get(state, ["qt", "config", "theme-attributes", "logo_url"], sketchesHost));
  return (
    <div className="logo-wrapper">
      <CustomLink
        href={logoUrl}
        callback={() => {
          props.closeAll();
          typeof props.windowScroll === "function" && props.windowScroll();
        }}
      >
        {secondaryLogo ? <img data-test-id="secondary_logo" src={secondaryLogo} alt="Logo" /> : <ImageFallback />}
      </CustomLink>
    </div>
  );
};
LogoWrapper.propTypes = {
  windowScroll: PropTypes.func,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object
};

export const Monogram = props => {
  const primaryLogo = get(props, ["primaryLogo"], "");
  const sketchesHost = useSelector(state => get(state, ["qt", "config", "sketches-host"], "/"));
  const logoUrl = useSelector(state => get(state, ["qt", "config", "theme-attributes", "logo_url"], sketchesHost));
  return (
    <div id="monogram">
      {
        <CustomLink
          href={logoUrl}
          callback={() => {
            this.props.closeAll();
            typeof this.props.windowScroll === "function" && this.props.windowScroll();
          }}
        >
          {primaryLogo ? <img src={primaryLogo} alt="Logo" /> : <ImageFallback />}
        </CustomLink>
      }
    </div>
  );
};

export const SecondaryMenu = ({ menu, publisherTheme, toggleSidebar, closeAll, customCssClasses = "" }) => (
  <Menu
    menu={getMenuData(menu, "secondary-menu", "")}
    languageDirection={get(publisherTheme, ["language_direction"], "left")}
    count={publisherTheme["secondary_menu_count"] || 4}
    toggleSidebar={toggleSidebar}
    closeAll={closeAll}
    customCssClasses={`menu-row ${customCssClasses}`}
    type="secondary"
  />
);
SecondaryMenu.propTypes = {
  menu: PropTypes.object,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object,
  toggleSidebar: PropTypes.func,
  customCssClasses: PropTypes.string,
  type: PropTypes.string
};

export const DefaultMenu = ({ menu, domainSlug, publisherTheme, closeAll, customCssClasses = "", isSingleLayer }) => (
  <Menu
    menu={getMenuData(menu, "default", domainSlug)}
    languageDirection={get(publisherTheme, ["language_direction"], "left")}
    count={publisherTheme["primary_menu_count"] || 6}
    closeAll={closeAll}
    customCssClasses={`menu-row ${customCssClasses}`}
    type="default"
    isSingleLayer={isSingleLayer}
  />
);
DefaultMenu.propTypes = {
  menu: PropTypes.object,
  domainSlug: PropTypes.string,
  closeAll: PropTypes.func,
  publisherTheme: PropTypes.object,
  customCssClasses: PropTypes.string,
  isSingleLayer: PropTypes.bool,
  type: PropTypes.string
};

export const Login = props => {
  const { openLoginTemplate, closeLoginTemplate, headerType = "" } = props;
  return (
    <WithMember>
      {({ member, logout, isLoading, checkForMemberUpdated }) => (
        <UserProfile
          openLoginTemplate={openLoginTemplate}
          closeLoginTemplate={closeLoginTemplate}
          member={member}
          logout={logout}
          isLoading={isLoading}
          checkForMemberUpdated={checkForMemberUpdated}
          customCssClasses={headerType}
        />
      )}
    </WithMember>
  );
};

Login.propTypes = {
  openLoginTemplate: PropTypes.bool,
  closeLoginTemplate: PropTypes.bool,
  headerType: PropTypes.string
};
